import {
	Box,
	Flex,
	Image,
	Input,
	InputGroup,
	Link,
	Show,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { LuEye, LuEyeOff } from 'react-icons/lu';
import { useNavigate } from 'react-router';
import * as yup from 'yup';
import FaceliftButton from '@/components/Atoms/FaceliftButton';
import FaceliftIconButton from '@/components/Atoms/FaceliftIconButton';
import FaceliftText from '@/components/Atoms/FaceliftText';
import OTPInput from '@/components/Molecules/OTPInput';
import CenteredCardLayout from '@/components/Organisms/CenteredCardLayout';
import { useColorMode } from '@/components/ui/color-mode-utils';
import {
	useCompletePasswordReset,
	useRequestPasswordResetOtp,
	useVerifyPasswordResetOtp,
} from '@/hooks/service-hooks/auth';

const mindsightLogo = '/svgs/mindsightLogo.svg';
const mindsightLogoWhite = '/svgs/mindsightLogoWhite.svg';

const emailSchema = yup.object({
	email: yup
		.string()
		.email('Please enter a valid email address')
		.required('Email is required'),
});

const passwordSchema = yup.object({
	password: yup
		.string()
		.required('New password is required')
		.min(8, 'Password must be at least 8 characters long')
		.matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
		.matches(/[a-z]/, 'Password must contain at least one lowercase letter')
		.matches(/\d/, 'Password must contain at least one number')
		.matches(
			/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/,
			'Password must contain at least one special character',
		),
	confirmPassword: yup
		.string()
		.required('Please confirm your new password')
		.oneOf([yup.ref('password')], 'Passwords must match'),
});

type EmailFormData = yup.InferType<typeof emailSchema>;
type PasswordFormData = yup.InferType<typeof passwordSchema>;

const ForgotPasswordPage = () => {
	const navigate = useNavigate();
	const { colorMode } = useColorMode();
	const darkMode = colorMode === 'dark';
	const [step, setStep] = useState<'email' | 'otp' | 'password'>('email');
	const [email, setEmail] = useState('');
	const [otp, setOtp] = useState('');
	const [resetToken, setResetToken] = useState('');
	const [resendTimer, setResendTimer] = useState(30);
	const [canResend, setCanResend] = useState(false);
	const [showPassword, setShowPassword] = useState(false);
	const [showConfirmPassword, setShowConfirmPassword] = useState(false);

	const {
		register: registerEmail,
		handleSubmit: handleEmailSubmit,
		formState: { errors: emailErrors },
	} = useForm<EmailFormData>({
		resolver: yupResolver(emailSchema),
		mode: 'onSubmit',
		defaultValues: {
			email,
		},
	});

	const {
		register: registerPassword,
		handleSubmit: handlePasswordSubmit,
		formState: { errors: passwordErrors },
	} = useForm<PasswordFormData>({
		resolver: yupResolver(passwordSchema),
		mode: 'onSubmit',
	});

	const { mutateAsync: requestPasswordResetOtp, isLoading: isRequestingOtp } =
		useRequestPasswordResetOtp(
			(data) => {
				setEmail(data.data.email);
				setOtp('');
				setResetToken('');
				setStep('otp');
				setResendTimer(30);
				setCanResend(false);
				toast.success(data.message || 'OTP sent successfully to your email.');
			},
			(error) => {
				const apiError = error as {
					response?: { data?: { message?: string } };
				};
				toast.error(
					apiError?.response?.data?.message ||
						'Failed to send OTP. Please try again.',
				);
			},
		);

	const { mutateAsync: verifyPasswordResetOtp, isLoading: isVerifyingOtp } =
		useVerifyPasswordResetOtp(
			(data) => {
				setResetToken(data.data.resetToken);
				setStep('password');
				toast.success(data.message || 'OTP verified successfully.');
			},
			(error) => {
				const apiError = error as {
					response?: { data?: { message?: string } };
				};
				toast.error(
					apiError?.response?.data?.message ||
						'Invalid OTP. Please try again.',
				);
			},
		);

	const {
		mutateAsync: completePasswordReset,
		isLoading: isCompletingPasswordReset,
	} = useCompletePasswordReset(
		() => {
			navigate('/login?reset=success', { replace: true });
		},
		(error) => {
			const apiError = error as {
				response?: { data?: { message?: string } };
			};
			toast.error(
				apiError?.response?.data?.message ||
					'Failed to update password. Please try again.',
			);
		},
	);

	useEffect(() => {
		if (step !== 'otp') {
			return;
		}

		if (resendTimer > 0) {
			const timer = setTimeout(() => {
				setResendTimer((prev) => prev - 1);
			}, 1000);

			return () => clearTimeout(timer);
		}

		setCanResend(true);
	}, [resendTimer, step]);

	const onRequestOtp = async (data: EmailFormData) => {
		const normalizedEmail = data.email.trim().toLowerCase();
		setEmail(normalizedEmail);
		await requestPasswordResetOtp({ email: normalizedEmail });
	};

	const onVerifyOtp = async () => {
		if (otp.length !== 6) {
			toast.error('Please enter the complete 6-digit OTP');
			return;
		}

		await verifyPasswordResetOtp({ email, otp });
	};

	const onResendOtp = async () => {
		if (!canResend || isRequestingOtp) {
			return;
		}

		await requestPasswordResetOtp({ email });
	};

	const onUpdatePassword = async (data: PasswordFormData) => {
		await completePasswordReset({
			email,
			password: data.password,
			resetToken,
		});
	};

	return (
		<CenteredCardLayout>
			<Flex flexDirection="column" gap={6}>
				<Flex justifyContent="center" mt={8}>
					<Image
						objectFit="contain"
						src={darkMode ? mindsightLogoWhite : mindsightLogo}
						alt="Mindsight Logo"
						width="240px"
						height="65px"
					/>
				</Flex>

				<Flex flexDirection="column" gap={1}>
					<FaceliftText
						fontSize="xl"
						fontWeight="bold"
						color="brand.textDark"
						textAlign="start"
					>
						{step === 'email' && 'Forgot Password'}
						{step === 'otp' && 'Verify Your Email'}
						{step === 'password' && 'Create a New Password'}
					</FaceliftText>
					<FaceliftText
						fontSize="sm"
						color="brand.textDark"
						textAlign="start"
					>
						{step === 'email' &&
							'Enter your email address and we will send a one-time verification code.'}
						{step === 'otp' &&
							`Enter the 6-digit code sent to ${email || 'your email address'}.`}
						{step === 'password' &&
							'Choose a strong password to finish resetting your account.'}
					</FaceliftText>
				</Flex>

				{step === 'email' && (
					<form onSubmit={handleEmailSubmit(onRequestOtp)}>
						<Flex flexDirection="column" gap={4}>
							<Box>
								<Input
									disabled={isRequestingOtp}
									{...registerEmail('email')}
									bg="brand.inputBg"
									variant="outline"
									size="md"
									placeholder="Email address"
									_placeholder={{
										color: { _dark: 'brand.lightGray' },
									}}
									borderColor={emailErrors.email ? 'brand.errors' : undefined}
									type="email"
								/>
								<Show when={emailErrors.email}>
									<FaceliftText color="brand.errors" fontSize="xs" mt={1} ml={1}>
										{emailErrors.email?.message}
									</FaceliftText>
								</Show>
							</Box>

							<FaceliftButton
								mt={2}
								type="submit"
								size="md"
								letterSpacing="widest"
								loading={isRequestingOtp}
								disabled={isRequestingOtp}
							>
								Send OTP
							</FaceliftButton>
						</Flex>
					</form>
				)}

				{step === 'otp' && (
					<Flex flexDirection="column" gap={4}>
						<Box>
							<OTPInput
								length={6}
								onChange={setOtp}
								value={otp}
								disabled={isVerifyingOtp}
							/>
						</Box>

						<FaceliftButton
							onClick={onVerifyOtp}
							size="md"
							letterSpacing="widest"
							loading={isVerifyingOtp}
							disabled={isVerifyingOtp || otp.length !== 6}
						>
							Verify OTP
						</FaceliftButton>

						<Flex flexDirection="column" gap={1} alignItems="center">
							<FaceliftText fontSize="sm" color="brand.textDark">
								Didn&apos;t receive a code?
							</FaceliftText>
								{canResend ? (
									<Link
										onClick={onResendOtp}
										color={{ _dark: 'brand.lightGray', _light: 'brand.solid' }}
										cursor={isRequestingOtp ? 'not-allowed' : 'pointer'}
										textDecoration="underline"
										fontSize="sm"
										opacity={isRequestingOtp ? 0.6 : 1}
									>
									{isRequestingOtp ? 'Resending...' : 'Resend code'}
								</Link>
								) : (
									<FaceliftText
										fontSize="sm"
										color={{ _dark: 'brand.lightGray', _light: 'brand.solid' }}
									>
										Resend code ({String(Math.floor(resendTimer / 60)).padStart(2, '0')}
										:
										{String(resendTimer % 60).padStart(2, '0')})
									</FaceliftText>
								)}
								<Link
									onClick={() => {
										setStep('email');
										setOtp('');
										setCanResend(false);
										setResendTimer(30);
									}}
									color={{ _dark: 'brand.lightGray', _light: 'brand.solid' }}
									cursor="pointer"
									textDecoration="underline"
									fontSize="sm"
								>
								Use a different email
							</Link>
						</Flex>
					</Flex>
				)}

				{step === 'password' && (
					<form onSubmit={handlePasswordSubmit(onUpdatePassword)}>
						<Flex flexDirection="column" gap={4}>
							<Box>
								<InputGroup
									endElement={
										<FaceliftIconButton
											bg="transparent"
											variant="ghost"
											aria-label="toggle new password visibility"
											size="xs"
											onClick={() => setShowPassword(!showPassword)}
											_hover={{
												bg: { _dark: 'gray.800', _light: 'gray.300' },
											}}
											color={{ _dark: 'brand', _light: 'brand.500' }}
										>
											{showPassword ? <LuEye size={16} /> : <LuEyeOff size={16} />}
										</FaceliftIconButton>
									}
								>
									<Input
										disabled={isCompletingPasswordReset}
										{...registerPassword('password')}
										variant="outline"
										bg="brand.inputBg"
										size="md"
										placeholder="New password"
										_placeholder={{
											color: { _dark: 'brand.lightGray' },
										}}
										type={showPassword ? 'text' : 'password'}
										borderColor={passwordErrors.password ? 'brand.errors' : undefined}
									/>
								</InputGroup>
								<Show when={passwordErrors.password}>
									<FaceliftText color="brand.errors" fontSize="xs" mt={1} ml={1}>
										{passwordErrors.password?.message}
									</FaceliftText>
								</Show>
							</Box>

							<Box>
								<InputGroup
									endElement={
										<FaceliftIconButton
											bg="transparent"
											variant="ghost"
											aria-label="toggle confirm password visibility"
											size="xs"
											onClick={() =>
												setShowConfirmPassword(!showConfirmPassword)
											}
											_hover={{
												bg: { _dark: 'gray.800', _light: 'gray.300' },
											}}
											color={{ _dark: 'brand', _light: 'brand.500' }}
										>
											{showConfirmPassword ? (
												<LuEye size={16} />
											) : (
												<LuEyeOff size={16} />
											)}
										</FaceliftIconButton>
									}
								>
									<Input
										disabled={isCompletingPasswordReset}
										{...registerPassword('confirmPassword')}
										variant="outline"
										bg="brand.inputBg"
										size="md"
										placeholder="Confirm new password"
										_placeholder={{
											color: { _dark: 'brand.lightGray' },
										}}
										type={showConfirmPassword ? 'text' : 'password'}
										borderColor={
											passwordErrors.confirmPassword ? 'brand.errors' : undefined
										}
									/>
								</InputGroup>
								<Show when={passwordErrors.confirmPassword}>
									<FaceliftText color="brand.errors" fontSize="xs" mt={1} ml={1}>
										{passwordErrors.confirmPassword?.message}
									</FaceliftText>
								</Show>
							</Box>

							<FaceliftText fontSize="xs" color="brand.textDark" textAlign="start">
								Your password must be at least 8 characters and include uppercase,
								lowercase, a number, and a special character.
							</FaceliftText>

							<FaceliftButton
								mt={2}
								type="submit"
								size="md"
								letterSpacing="widest"
								loading={isCompletingPasswordReset}
								disabled={isCompletingPasswordReset}
							>
								Update Password
							</FaceliftButton>
						</Flex>
					</form>
				)}

				<Flex justifyContent="center">
					<Link
						onClick={() => navigate('/login')}
						color={{ _dark: 'brand.lightGray', _light: 'brand.solid' }}
						cursor="pointer"
						textDecoration="underline"
						fontSize="sm"
					>
						Back to Login
					</Link>
				</Flex>
			</Flex>
		</CenteredCardLayout>
	);
};

export default ForgotPasswordPage;
